import { gql } from '@apollo/client';

// Documentos GraphQL de organizaciones (panel admin) + auto-vinculación por código (cliente).
// Se consumen con los hooks useQuery/useMutation de Apollo (mismo patrón que cupones).

export const ORGANIZATIONS = gql`
  query Organizations($first: Int!, $page: Int, $search: String) {
    organizations(first: $first, page: $page, search: $search) {
      data {
        id
        name
        join_code
        contact_email
        contact_name
        is_default
        is_active
        company_covered_percent
        notes
        benefits
        members_count
      }
      paginatorInfo { currentPage lastPage total }
    }
  }
`;

export const ORGANIZATION = gql`
  query Organization($id: ID!) {
    organization(id: $id) {
      id
      name
      join_code
      contact_email
      contact_name
      is_default
      is_active
      company_covered_percent
      notes
      benefits
      cover_url
      logo_url
      members_count
      plan_discounts {
        plan_id
        plan_name
        client_discount_percent
        is_active
      }
    }
  }
`;

// Miembros de una organización (para la tabla y el board de arrastre).
export const ORGANIZATION_MEMBERS = gql`
  query OrganizationMembers($organization_id: [Int!], $first: Int!, $page: Int) {
    users(role: CLIENT, organization_id: $organization_id, first: $first, page: $page) {
      data {
        id
        full_name
        email
        membership_status
        plan_id { id name }
        department { id name }
        organization_joined_at
        created_at
      }
      paginatorInfo { currentPage lastPage total }
    }
  }
`;

// Planes disponibles para asociar a la organización (los que pasan por checkout de suscripción).
export const ORGANIZATION_ASSIGNABLE_PLANS = gql`
  query OrganizationAssignablePlans {
    plans(first: 100, page: 1) {
      data {
        id
        name
        price_cop
        is_subscribable
        subscription_interval
      }
    }
  }
`;

export const CREATE_ORGANIZATION = gql`
  mutation CreateOrganization($input: CreateOrganizationInput!) {
    createOrganization(input: $input) {
      id
      name
      join_code
    }
  }
`;

export const UPDATE_ORGANIZATION = gql`
  mutation UpdateOrganization($id: ID!, $input: UpdateOrganizationInput!) {
    updateOrganization(id: $id, input: $input) {
      id
      name
    }
  }
`;

// Portada de la empresa: es el fondo del hero de SU panel, así que se sube desde el detalle
// (aquí ya existe la entidad; el formulario de creación todavía no tiene id al que subirla).
export const UPLOAD_ORGANIZATION_COVER = gql`
  mutation UploadOrganizationCover($id: ID!, $image: Upload!) {
    uploadOrganizationCover(id: $id, image: $image) {
      id
      cover_url
    }
  }
`;

export const REMOVE_ORGANIZATION_COVER = gql`
  mutation RemoveOrganizationCover($id: ID!) {
    removeOrganizationCover(id: $id) {
      id
      cover_url
    }
  }
`;

// El logo va SEPARADO de la portada: se renderiza contenido (nunca recortado) y se guarda en
// PNG para conservar la transparencia.
export const UPLOAD_ORGANIZATION_LOGO = gql`
  mutation UploadOrganizationLogo($id: ID!, $image: Upload!) {
    uploadOrganizationLogo(id: $id, image: $image) {
      id
      logo_url
    }
  }
`;

export const REMOVE_ORGANIZATION_LOGO = gql`
  mutation RemoveOrganizationLogo($id: ID!) {
    removeOrganizationLogo(id: $id) {
      id
      logo_url
    }
  }
`;

export const DELETE_ORGANIZATIONS = gql`
  mutation DeleteOrganizations($id: [ID!]!) {
    deleteOrganizations(id: $id) {
      id
    }
  }
`;

export const SET_ORGANIZATION_PLANS = gql`
  mutation SetOrganizationPlans($organization_id: ID!, $plans: [OrgPlanInput!]!) {
    setOrganizationPlans(organization_id: $organization_id, plans: $plans) {
      id
      plan_discounts {
        plan_id
        plan_name
        client_discount_percent
        is_active
      }
    }
  }
`;

export const ASSIGN_USERS_TO_ORGANIZATION = gql`
  mutation AssignUsersToOrganization($organization_id: ID!, $user_ids: [ID!]!) {
    assignUsersToOrganization(organization_id: $organization_id, user_ids: $user_ids) {
      id
      members_count
    }
  }
`;

export const REMOVE_USERS_FROM_ORGANIZATION = gql`
  mutation RemoveUsersFromOrganization($user_ids: [ID!]!) {
    removeUsersFromOrganization(user_ids: $user_ids)
  }
`;

export const IMPORT_ORGANIZATION_MEMBERS = gql`
  mutation ImportOrganizationMembers($organization_id: ID!, $members: [OrgMemberInput!]!) {
    importOrganizationMembers(organization_id: $organization_id, members: $members) {
      created
      linked_existing
      skipped { row email reason }
      created_members { name email }
    }
  }
`;

export const GRANT_ORGANIZATION_MEMBERSHIPS = gql`
  mutation GrantOrganizationMemberships($organization_id: ID!, $plan_ids: [ID!]!, $user_ids: [ID!]!, $period_months: Int) {
    grantOrganizationMemberships(organization_id: $organization_id, plan_ids: $plan_ids, user_ids: $user_ids, period_months: $period_months) {
      granted
      skipped
    }
  }
`;

// Envía el acceso (contraseña temporal + enlace) a los miembros seleccionados.
export const SEND_ORGANIZATION_ACCESS = gql`
  mutation SendOrganizationAccess($organization_id: ID!, $user_ids: [ID!]!) {
    sendOrganizationAccess(organization_id: $organization_id, user_ids: $user_ids) {
      sent
      skipped
    }
  }
`;

export const REGENERATE_ORGANIZATION_CODE = gql`
  mutation RegenerateOrganizationCode($id: ID!) {
    regenerateOrganizationCode(id: $id) {
      id
      join_code
    }
  }
`;

export const RESEND_ORGANIZATION_CODE = gql`
  mutation ResendOrganizationCode($id: ID!) {
    resendOrganizationCode(id: $id)
  }
`;

// Cliente autenticado: se vincula con el código de su organización.
export const JOIN_ORGANIZATION_BY_CODE = gql`
  mutation JoinOrganizationByCode($code: String!) {
    joinOrganizationByCode(code: $code) {
      id
      name
    }
  }
`;
